import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Lock, Smartphone, Sparkles, Store, UserPlus, BadgeCheck, LogOut, Ticket } from "lucide-react";
import { PortalHeader, PortalFooter } from "./NassauToday";
import { MemberCard, ClaimScreen } from "../components/MemberCard";
import MemberAuthModal from "../components/MemberAuthModal";
import LoyaltyBadge from "../components/LoyaltyBadge";
import { getLoyaltyOffers, getLoyaltyMerchants, memberMe, memberClaims, claimOffer, hasMemberToken, memberSignOut, apiError } from "../lib/member";

export default function Loyalty() {
  const [data, setData] = useState({ daily: null, offers: [] });
  const [merchants, setMerchants] = useState([]);
  const [member, setMember] = useState(null);
  const [claims, setClaims] = useState([]);
  const [auth, setAuth] = useState(null);
  const [pending, setPending] = useState(null);
  const [active, setActive] = useState(null);

  const loadMember = () => memberMe().then(setMember).then(() => memberClaims().then(setClaims)).catch(() => { memberSignOut(); setMember(null); });
  useEffect(() => {
    getLoyaltyOffers().then(setData).catch(() => {});
    getLoyaltyMerchants().then(setMerchants).catch(() => {});
    if (hasMemberToken()) loadMember();
  }, []);

  const doClaim = async (offer, m = member) => {
    if (!m) { setPending(offer); setAuth("join"); return; }
    try { const c = await claimOffer(offer.id); setActive(c); memberClaims().then(setClaims); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onAuthed = (m) => { setMember(m); setAuth(null); memberClaims().then(setClaims).catch(() => {}); toast.success(`Welcome, ${m.name.split(" ")[0]}!`); if (pending) { if (pending.subscriber_only && !m.is_subscriber) toast.info("That one is a subscriber perk. Subscribe to the News-Leader to unlock it."); else doClaim(pending, m); setPending(null); } };
  const signOut = () => { memberSignOut(); setMember(null); setClaims([]); };

  return (
    <div className="portal" data-testid="loyalty-page">
      <PortalHeader />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:py-8">
        <Hero member={member} onJoin={() => setAuth("join")} onLogin={() => setAuth("login")} onSignOut={signOut} />
        <HowItWorks />
        {data.daily && <DailyDealFeature deal={data.daily} onClaim={doClaim} />}
        <OffersGrid offers={data.offers} member={member} onClaim={doClaim} />
        <Merchants merchants={merchants} />
        {member && <MyClaims claims={claims} onOpen={setActive} />}
      </main>
      <PortalFooter />
      {auth && <MemberAuthModal initialMode={auth} onClose={() => { setAuth(null); setPending(null); }} onDone={onAuthed} />}
      {active && <ClaimScreen claim={active} member={member} onClose={() => setActive(null)} />}
    </div>
  );
}

function Hero({ member, onJoin, onLogin, onSignOut }) {
  return (
    <section className="p-fade mb-8 grid items-center gap-8 overflow-hidden rounded-3xl bg-[#0B2433] px-6 py-10 text-white sm:px-10 md:grid-cols-5">
      <div className="md:col-span-3">
        <p className="portal-chip flex items-center gap-1.5 text-[var(--p-amber)]"><Sparkles size={13} /> Nassau Life Loyalty Club</p>
        <h1 className="font-portal mt-3 text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">Local perks, right on your phone.</h1>
        <p className="mt-4 max-w-xl text-sm text-white/75 sm:text-base">Free for everyone. Claim a deal, show your phone at the counter and save at merchants across Amelia Island and Nassau County. Newspaper subscribers unlock extra perks automatically.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          {member ? (
            <>
              <a href="#offers" data-testid="loyalty-browse-offers" className="lift rounded-full bg-[var(--p-amber)] px-6 py-3 text-sm font-bold text-[var(--p-ink)]">Browse offers</a>
              <button onClick={onSignOut} data-testid="member-signout-button" className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-5 py-3 text-sm font-semibold hover:bg-white/10"><LogOut size={15} /> Sign out</button>
            </>
          ) : (
            <>
              <button onClick={onJoin} data-testid="loyalty-join-button" className="lift rounded-full bg-[var(--p-amber)] px-6 py-3 text-sm font-bold text-[var(--p-ink)]">Join free</button>
              <button onClick={onLogin} data-testid="loyalty-signin-button" className="rounded-full border border-white/25 px-6 py-3 text-sm font-semibold hover:bg-white/10">Sign in</button>
            </>
          )}
        </div>
        {member && !member.is_subscriber && <p className="mt-4 text-xs text-white/60">Subscribe to the News-Leader to unlock subscriber perks. <Link to="/subscribe" className="font-semibold text-[var(--p-amber)] hover:underline">Subscribe →</Link></p>}
      </div>
      <div className="md:col-span-2"><div className="mx-auto max-w-sm rotate-[-3deg] transition-transform duration-500 hover:rotate-0"><MemberCard member={member} /></div></div>
    </section>
  );
}

const STEPS = [
  { icon: UserPlus, title: "Join free", text: "Create your account in seconds. Subscribers are recognized automatically." },
  { icon: Ticket, title: "Claim a deal", text: "Tap Claim on any offer from a participating merchant." },
  { icon: Smartphone, title: "Show your phone", text: "The cashier sees your member card and code. That's it." },
];
function HowItWorks() {
  return (
    <section className="mb-10 grid gap-4 sm:grid-cols-3" data-testid="loyalty-how-it-works">
      {STEPS.map((s, i) => (
        <div key={s.title} className="portal-card p-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--p-teal)]/10 text-[var(--p-teal)]"><s.icon size={19} /></span>
          <p className="font-portal mt-3 text-lg font-extrabold text-[var(--p-ink)]">{i + 1}. {s.title}</p>
          <p className="mt-1 text-sm text-slate-500">{s.text}</p>
        </div>
      ))}
    </section>
  );
}

function DailyDealFeature({ deal, onClaim }) {
  return (
    <section data-testid="loyalty-daily-deal" className="mb-10 grid overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 md:grid-cols-2">
      {deal.image && <img src={deal.image} alt={deal.title} className="h-56 w-full object-cover md:h-full" />}
      <div className="flex flex-col justify-center p-7">
        <p className="portal-chip flex items-center gap-1.5 text-[var(--p-clay)]"><Flame size={13} /> Today's Daily Deal</p>
        <p className="font-portal mt-2 text-3xl font-black text-[var(--p-ink)]">{deal.title}</p>
        <p className="mt-1 text-sm font-semibold text-slate-500">{deal.business_name}</p>
        {deal.description && <p className="mt-3 text-sm text-slate-600">{deal.description}</p>}
        <div className="mt-5 flex items-center gap-4">
          {deal.discount && <span className="font-portal text-3xl font-black text-[var(--p-teal)]">{deal.discount}</span>}
          <button onClick={() => onClaim(deal)} data-testid="claim-daily-deal-button" className="lift rounded-full bg-[var(--p-teal)] px-6 py-2.5 text-sm font-bold text-white hover:bg-[var(--p-teal-bright)]">Claim deal</button>
        </div>
      </div>
    </section>
  );
}

function OffersGrid({ offers, member, onClaim }) {
  return (
    <section id="offers" className="mb-10 scroll-mt-24">
      <p className="portal-chip text-[var(--p-teal-bright)]">Member offers</p>
      <h2 className="font-portal text-2xl font-extrabold tracking-tight text-[var(--p-ink)] sm:text-3xl">Special offers from local merchants</h2>
      <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {offers.map((o) => <OfferCard key={o.id} o={o} member={member} onClaim={onClaim} />)}
        {offers.length === 0 && <p className="text-sm text-slate-400">New offers are coming soon.</p>}
      </div>
    </section>
  );
}

function OfferCard({ o, member, onClaim }) {
  const locked = o.subscriber_only && member && !member.is_subscriber;
  return (
    <div data-testid={`loyalty-offer-${o.id}`} className="portal-card flex flex-col overflow-hidden">
      <div className="relative h-32 overflow-hidden bg-slate-100">
        {o.image && <img src={o.image} alt={o.business_name} className="h-full w-full object-cover" />}
        {o.subscriber_only && <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[var(--p-ink)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--p-amber)]"><BadgeCheck size={11} /> Subscriber perk</span>}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{o.business_name}</p>
        <p className="font-portal mt-1 text-lg font-extrabold leading-tight text-[var(--p-ink)]">{o.title}</p>
        <p className="mt-1 flex-1 text-xs text-slate-500">{o.description}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="font-portal text-xl font-black text-[var(--p-teal)]">{o.discount}</span>
          {locked ? (
            <Link to="/subscribe" data-testid={`unlock-offer-${o.id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50"><Lock size={12} /> Subscribe to unlock</Link>
          ) : (
            <button onClick={() => onClaim(o)} data-testid={`claim-offer-${o.id}`} className="rounded-full bg-[var(--p-teal)] px-4 py-1.5 text-xs font-bold text-white hover:bg-[var(--p-teal-bright)]">Claim</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Merchants({ merchants }) {
  if (!merchants.length) return null;
  return (
    <section className="mb-10" data-testid="loyalty-merchants">
      <p className="portal-chip text-[var(--p-teal-bright)]">Participating merchants</p>
      <h2 className="font-portal text-2xl font-extrabold tracking-tight text-[var(--p-ink)] sm:text-3xl">Where to use your card</h2>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {merchants.map((b) => (
          <Link key={b.id} to="/directory" className="portal-card flex items-center gap-3 p-3">
            {b.image ? <img src={b.image} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--p-teal)]/10 text-[var(--p-teal)]"><Store size={18} /></span>}
            <div className="min-w-0"><p className="truncate text-sm font-bold text-[var(--p-ink)]">{b.name}</p><LoyaltyBadge className="mt-1" /></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function MyClaims({ claims, onOpen }) {
  return (
    <section className="mb-6" data-testid="my-claims">
      <h2 className="font-portal text-2xl font-extrabold tracking-tight text-[var(--p-ink)]">My claimed deals</h2>
      <div className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
        {claims.map((c) => (
          <button key={c.id} onClick={() => onOpen(c)} data-testid={`my-claim-${c.id}`} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50">
            <div><p className="text-sm font-bold text-[var(--p-ink)]">{c.offer_title}</p><p className="text-xs text-slate-400">{c.business_name} · {new Date(c.claimed_at).toLocaleDateString()}</p></div>
            <span className="font-mono text-sm font-bold tracking-widest text-[var(--p-teal)]">{c.code}</span>
          </button>
        ))}
        {claims.length === 0 && <p className="px-5 py-4 text-sm text-slate-400">You haven't claimed any deals yet.</p>}
      </div>
    </section>
  );
}
